import dayjs from 'dayjs';
import { Card, Col, Form, Input, Row } from 'antd';
import { SelectPlus } from 'Components/SelectPlus';
import { InputNumberPlus } from '@shared/components/inputs/InputNumberPlus';
import { InputDate } from 'Components/Inputs/InputDate';
import { capitalize } from '@shared/utils/stringUtils';
import { INVALID_DATE_MESSAGE } from '@shared/constants/date';
import { DOCUMENT_TYPE_OPTIONS } from '@constants/documentTypeOptions';
import { GENDER_OPTIONS } from '@constants/genderOptions';

export const PERSON_ROLES = {
  TAKER: 'taker',
  BENEFICIARY: 'beneficiary',
};

const PERSON_FORM_FIELDS = {
  DOCUMENT_TYPE: 'documentType',
  DOCUMENT_NUMBER: 'documentNumber',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  DATE_OF_BIRTH: 'dateOfBirth',
  GENDER: 'gender',
  PHONE_NUMBER: 'phoneNumber',
  EMAIL: 'email',
};

const getTitleByRole = (role) => {
  const roleToTitleMap = {
    [PERSON_ROLES.TAKER]: 'Información del tomador',
    [PERSON_ROLES.BENEFICIARY]: 'Información del beneficiario',
  };

  return roleToTitleMap[role];
};

const validatePhoneNumber = (value) => {
  if (!value) return Promise.resolve();
  if (!/^3/.test(value)) return Promise.reject('El primer dígito debe ser 3');
  if (!/^\d{10}$/.test(value)) return Promise.reject('Debe tener un total de 10 dígitos');

  return Promise.resolve();
};

const validateDocumentNumber = (value, { documentType }) => {
  if (!value) return Promise.resolve();

  if (documentType === 'NIT') {
    const nitParts = value.split('-'); // Separa el NIT por el guión

    if (nitParts.length > 2) {
      return Promise.reject('Debe contener unicamente un guión');
    }

    if (nitParts.length !== 2) {
      return Promise.reject('Falta el dígito de verificación');
    }

    const [mainPart, checkDigit] = nitParts;

    if (!/^\d{1,10}$/.test(mainPart)) {
      return Promise.reject('Debe tener máximo 10 dígitos antes del guión');
    }

    if (!/^\d$/.test(checkDigit)) {
      return Promise.reject('El dígito de verificación debe estar entre 0 y 9');
    }
  }

  if (documentType === 'CC' || documentType === 'TI' || documentType === 'CE') {
    if (!/^\d+$/.test(value)) {
      return Promise.reject('Debe contener solo dígitos');
    }
  }

  if (documentType === 'CC' || documentType === 'TI') {
    if (!/^\d{1,10}$/.test(value)) {
      return Promise.reject('Debe tener máximo 10 dígitos');
    }
  }

  return Promise.resolve();
};

const validateDateOfBirth = (value, { role, isCompany }) => {
  if (!value) return Promise.resolve();

  const today = dayjs();
  const date = dayjs(value);

  if (!date.isValid()) {
    return Promise.reject(INVALID_DATE_MESSAGE)
  }

  if (role === PERSON_ROLES.TAKER && !isCompany) {
    const age = today.diff(date, 'year');
    if (age < 18) return Promise.reject('Debe ser mayor de edad');
  } else {
    if (date.isAfter(today)) {
      return Promise.reject('Debe ser menor o igual a la fecha actual');
    }
  }

  return Promise.resolve();
};

/**
 * @param {object} props
 * @param {'taker' | 'beneficiary'} props.role Rol de la persona en el formulario.
 */
export const PersonForm = ({ form, role, header, footer, disabled, showTitle = true, cardProps = {}, ...formProps }) => {
  const documentType = Form.useWatch(PERSON_FORM_FIELDS.DOCUMENT_TYPE, form);
  const isCompany = documentType === 'NIT';

  /** Ajusta el placeholder si el formulario está deshabilitado (remueve 'Ingresar'/'Seleccionar' y capitaliza). */
  const getPlaceholder = (text) => {
    if (!disabled) return text;

    return capitalize(
      text
        .replace(/^(seleccionar|ingresar)/i, '')
        .trim()
    )
  };

  return (
    <Card
      title={showTitle ? getTitleByRole(role) : undefined}
      {...cardProps}
      styles={{
        ...cardProps.styles,
        header: {
          textAlign: 'center',
          ...cardProps.styles?.header,
        },
      }}
    >
      {header}
      <Form
        layout='vertical'
        form={form}
        disabled={disabled}
        {...formProps}
      >
        <Row gutter={16} align={'bottom'}>
          <Col xs={8}>
            <Form.Item
              label='Tipo de documento'
              name={PERSON_FORM_FIELDS.DOCUMENT_TYPE}
              rules={[{ required: true }]}
            >
              <SelectPlus options={DOCUMENT_TYPE_OPTIONS} placeholder={getPlaceholder('Seleccionar tipo de documento')} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label='Número de documento'
              name={PERSON_FORM_FIELDS.DOCUMENT_NUMBER}
              rules={[
                { required: true },
                ({ getFieldValue }) => {
                  const currentDocumentType = getFieldValue(PERSON_FORM_FIELDS.DOCUMENT_TYPE);
                  return { validator: (_, value) => validateDocumentNumber(value, { documentType: currentDocumentType }) }
                },
              ]}
              hasFeedback
            >
              <Input placeholder={getPlaceholder('Ingresar número de documento')} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label={isCompany ? 'Razón social' : 'Nombres'}
              name={PERSON_FORM_FIELDS.FIRST_NAME}
              rules={[{ required: true }]}
            >
              <Input placeholder={getPlaceholder(`Ingresar ${isCompany ? 'razón social' : 'nombres'}`)} />
            </Form.Item>
          </Col>
          {!isCompany && (
            <Col xs={8} >
              <Form.Item
                label='Apellidos'
                name={PERSON_FORM_FIELDS.LAST_NAME}
                rules={[{ required: true }]}
              >
                <Input placeholder={getPlaceholder('Ingresar apellidos')} />
              </Form.Item>
            </Col>
          )}
          <Col xs={8} >
            <Form.Item
              label={`Fecha de ${isCompany ? 'constitución' : 'nacimiento'}`}
              name={PERSON_FORM_FIELDS.DATE_OF_BIRTH}
              rules={[
                { required: true },
                { validator: (_, value) => validateDateOfBirth(value, { role, isCompany }) },
              ]}
              hasFeedback
            >
              <InputDate placeholder={getPlaceholder(`Ingresar fecha de ${isCompany ? 'constitución' : 'nacimiento'}`)} showNow={false} />
            </Form.Item>
          </Col>
          {!isCompany && (
            <Col xs={8} >
              <Form.Item
                label='Genero'
                name={PERSON_FORM_FIELDS.GENDER}
                rules={[{ required: true }]}
              >
                <SelectPlus options={GENDER_OPTIONS} placeholder={getPlaceholder('Ingresar genero')} />
              </Form.Item>
            </Col>
          )}
          <Col xs={8}>
            <Form.Item
              label='Número de celular'
              name={PERSON_FORM_FIELDS.PHONE_NUMBER}
              rules={[
                { required: true },
                { validator: (_, value) => validatePhoneNumber(value) },
              ]}
              hasFeedback
            >
              <InputNumberPlus placeholder={getPlaceholder('Ingresar número de celular')} numberType='positive' precision={0} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label='Correo electrónico'
              name={PERSON_FORM_FIELDS.EMAIL}
              rules={[
                { required: true },
                { type: 'email' },
              ]}
              hasFeedback
            >
              <Input placeholder={getPlaceholder('Ingresar correo electrónico')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {footer}
    </Card>
  );
};